import React, {
    ReactElement, useEffect, useMemo, useRef, useState,
} from "react";
import { Toast } from "react-bootstrap";

import Styles from "./TimedToast.module.scss";

interface Props {
    title: string;
    children: ReactElement;
    seconds?: number;
    canCancel?: boolean;
    onCancel?: () => void;
    onClose?: () => void;
    shouldShow?: boolean
}

interface ReferenceState {
    onClose?:() => void;
    isFinished: boolean;
    isCanceled: boolean;
}

export default function TimedToast({
    title, children, seconds, onClose, canCancel, onCancel, shouldShow,
}: Props): ReactElement | null {
    const [timeleft, setTimeLeft] = useState(seconds ?? 5);
    const completedReference = useMemo<ReferenceState>(() => ({
        isFinished: false,
        isCanceled: false,
        onClose,
    }), []);

    completedReference.onClose = onClose;

    useEffect(() => {
        if (seconds && shouldShow) {
            completedReference.isCanceled = false;
            completedReference.isFinished = false;
            setTimeLeft(seconds);
        }
    }, [seconds, shouldShow]);

    const handleClose = () => {
        setTimeLeft(0);
        completedReference.isFinished = true;
        completedReference.onClose?.();
    };

    useEffect(() => {
        if (timeleft === 0 && !completedReference.isCanceled) {
            completedReference.isFinished = true;
            completedReference.onClose?.();
        } else if (timeleft > 0) {
            setTimeout(() => {
                setTimeLeft(current => Math.max(current - 1, 0));
            }, 1000);
        }
    }, [timeleft]);

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            if (!completedReference.isFinished && !completedReference.isCanceled) {
                handleClose();
            }
        };
    }, []);

    if (timeleft <= 0) {
        return null;
    }

    const handleCancel = () => {
        setTimeLeft(0);
        completedReference.isCanceled = true;
        completedReference.isFinished = true;
        onCancel?.();
    };

    return (

        <Toast onClose={handleClose} show={shouldShow ?? true} className={Styles.toast}>
            <Toast.Header>
                <strong className="me-auto">
                    {title}
                </strong>
                <small>
                    Closing in
                    {" "}
                    {timeleft}
                </small>
            </Toast.Header>
            <Toast.Body>
                <div className="d-flex justify-content-between p-2">
                    {children}
                    {canCancel && (
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="btn btn-none btn-sm"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </Toast.Body>
        </Toast>

    );
}

TimedToast.defaultProps = {
    seconds: 5,
    onClose: undefined,
    canCancel: false,
    onCancel: undefined,
    shouldShow: undefined,
};
